import { useCallback, useState } from "react";

/**
 * Return type for the pending question hook
 */
interface UsePendingQuestionReturn {
  /** The current pending question text */
  pendingQuestion: string | null;
  /** Whether to push the pending question to the top */
  pushUpPendingQuestion: boolean;
  /** Sets a new pending question and pushes it to the top */
  setPendingQuestionWithPushUp: (_question: string) => void;
  /** Clears the pending question */
  clearPendingQuestion: () => void;
  /** Resets the push-up state */
  resetPushUp: () => void;
}

/**
 * Custom hook for managing pending question state in the chat thread.
 * Handles the state of questions that are currently being processed.
 *
 * Features:
 * - Tracks the current pending question text
 * - Manages the "push up" state for scrolling behavior
 * - Provides utilities for setting and clearing pending questions
 *
 * @returns Pending question state and management functions
 *
 * @example
 * const {
 *   pendingQuestion,
 *   pushUpPendingQuestion,
 *   setPendingQuestionWithPushUp,
 *   clearPendingQuestion
 * } = usePendingQuestion();
 *
 * // When user sends a message
 * setPendingQuestionWithPushUp("What is the project timeline?");
 *
 * // When streaming completes
 * clearPendingQuestion();
 */
export function usePendingQuestion(): UsePendingQuestionReturn {
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const [pushUpPendingQuestion, setPushUpPendingQuestion] = useState<boolean>(false);

  /**
   * Sets a new pending question and enables push-up mode
   */
  const setPendingQuestionWithPushUp = useCallback((question: string) => {
    setPendingQuestion(question);
    setPushUpPendingQuestion(true);
  }, []);

  /**
   * Clears the pending question
   */
  const clearPendingQuestion = useCallback(() => {
    setPendingQuestion(null);
  }, []);

  /**
   * Resets the push-up state without clearing the question
   */
  const resetPushUp = useCallback(() => {
    setPushUpPendingQuestion(false);
  }, []);

  return {
    pendingQuestion,
    pushUpPendingQuestion,
    setPendingQuestionWithPushUp,
    clearPendingQuestion,
    resetPushUp,
  };
}
